import { create } from 'zustand';
import fetchWithTenant from '../utils/fetchWithTenant';

// Data layer for the ONVIF analytics event timeline ([6.2]). Flat Zustand store
// mirroring raLicenseStore. Tenant identity rides the x-tenant-id header that
// fetchWithTenant injects from localStorage.currentTenantId, so tenantId is
// NEVER placed in the querystring here.

const PAGE_SIZE = 50;

// The filterable fields the [5.3] /api/analytics/events endpoint accepts.
// Kept as a flat object so the (later) filter panel can setFilter(key, value).
// EMPTY_FILTERS is the UNBOUNDED ("All time") shape; the default view uses
// getDefaultFilters() (a rolling 30d window) so it hits the fast bounded path.
const EMPTY_FILTERS = {
  cameraId: '',
  canonicalTopic: '',
  eventCategory: '',
  vendor: '',
  from: '',
  to: '',
};

// [8.3a] Default the analytics window to the last 30 days. Unbounded queries
// full-scan the tenant partition (2s histogram / 35s facets at 2M); a bounded
// default hits a parallel index scan (sub-second). 'All time' is opt-in.
const DEFAULT_WINDOW_DAYS = 30;

// Browser-local 'YYYY-MM-DDTHH:mm' (datetime-local format). from/to are stored
// local and converted to instants in buildQuery; this matches that contract.
function toLocalInput(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

// Computed at CALL time (not module-eval) so a long-open tab stays current.
function defaultWindow() {
  const now = new Date();
  const from = new Date(now.getTime() - DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  return { from: toLocalInput(from), to: toLocalInput(now) };
}

function getDefaultFilters() {
  return { ...EMPTY_FILTERS, ...defaultWindow() };
}

// Build a querystring from the non-empty filters plus paging params. tenantId
// is intentionally absent (header carries it); empty filters are omitted.
//
// from/to are stored as browser-LOCAL 'YYYY-MM-DDTHH:mm' strings (datetime-local
// inputs + bar-click). We convert them to an unambiguous UTC instant before
// sending: new Date(bareString) parses as browser-local, and .toISOString()
// emits the Z instant, so the backend (which reparses in its own zone) lands on
// exactly the local moment the user picked. This is the core [6.4a] fix.
function buildQuery(filters, { cursor, sort } = {}) {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || String(value).trim() === '') continue;
    if (key === 'from' || key === 'to') {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) continue;
      qs.set(key, d.toISOString());
    } else {
      qs.set(key, value);
    }
  }
  qs.set('limit', String(PAGE_SIZE));
  // sort flows to the events endpoint so load-more continues in the chosen
  // order across pages (the cursor comparator depends on it). Aggregate calls
  // omit it. Always sent (incl. 'desc') so the param is unambiguous.
  if (sort) qs.set('sort', sort);
  if (cursor) qs.set('cursor', cursor);
  return qs.toString();
}

// eventMatchesFilters mirrors the server-side buildEventWhere predicate for the
// UI's filter set, so live-streamed rows are admitted to the list iff they would
// have come back from GET /events under the same filters. from/to are the
// browser-local 'YYYY-MM-DDTHH:mm' strings; new Date() parses them as local,
// matching how buildQuery converts them to instants. Range is inclusive
// [from, to], matching the list query (Prisma gte/lte).
function eventMatchesFilters(e, f) {
  if (!e) return false;
  if (f.cameraId && e.cameraId !== f.cameraId) return false;
  if (f.canonicalTopic && e.canonicalTopic !== f.canonicalTopic) return false;
  if (f.eventCategory && e.eventCategory !== f.eventCategory) return false;
  if (f.vendor && e.vendor !== f.vendor) return false;
  const ts = e.receivedAt ? new Date(e.receivedAt).getTime() : NaN;
  if (f.from) {
    const from = new Date(f.from).getTime();
    if (!Number.isNaN(from) && (Number.isNaN(ts) || ts < from)) return false;
  }
  if (f.to) {
    const to = new Date(f.to).getTime();
    if (!Number.isNaN(to) && (Number.isNaN(ts) || ts > to)) return false;
  }
  return true;
}

// Filter-dropdown vocabulary ([8.3b]). Sourced from the cheap /facet-values
// endpoint (enum + VendorTopicMapping), NOT from aggregating events. cameraId
// options come from the devices/cameraNames map (see AnalyticsFilterPanel), so
// there is no `cameras` key here.
const EMPTY_FACETS = { categories: [], vendors: [], topics: [] };

const useAnalyticsStore = create((set, get) => ({
  filters: getDefaultFilters(), // [8.3a] default to the last 30 days
  facets: { ...EMPTY_FACETS },
  events: [],
  nextCursor: null,
  hasMore: false,
  loading: false,
  error: null,
  // Received-date sort direction ([6.9]); 'desc' = newest first.
  sort: 'desc',
  // Event detail drawer ([6.6]): id of the selected row (rendered from the
  // already-loaded events array, no fetch). cameraNames maps the analytics
  // cameraId (== MdDiscoveredDevice.id) to a human label.
  selectedEventId: null,
  cameraNames: {},
  cameraNamesLoaded: false,
  // Embedded camera-scoped mode (868jww2aq): when set, the surface is locked to
  // one cameraId - the Camera filter control hides and Reset keeps this scope.
  lockedCameraId: null,
  // Histogram buckets ([6.4]): [{ bucketStart, count }] over the SAME filters.
  aggregate: [],
  aggLoading: false,
  aggError: null,

  // Changing a filter resets paging and refetches the first page (a fresh read,
  // never an append). fetchEvents reads the just-updated filters from get().
  setFilter: (key, value) => {
    set((state) => ({ filters: { ...state.filters, [key]: value } }));
    get().fetchEvents();
  },

  resetFilters: () => {
    // Reset to the DEFAULT (30d window), not all-time, EXCEPT a locked camera
    // scope: in embedded camera mode the surface stays pinned to its cameraId.
    const { lockedCameraId } = get();
    const next = getDefaultFilters();
    if (lockedCameraId) next.cameraId = lockedCameraId;
    set({ filters: next });
    get().fetchEvents();
  },

  // Window controls ([8.3a]): keep the other active filters, change only the
  // time bounds. setDefaultWindow -> rolling 30d (fast path); setAllTime ->
  // unbounded (explicit opt-in to the full-scan path).
  setDefaultWindow: () => {
    const w = defaultWindow();
    set((state) => ({ filters: { ...state.filters, from: w.from, to: w.to } }));
    get().fetchEvents();
  },

  setAllTime: () => {
    set((state) => ({ filters: { ...state.filters, from: '', to: '' } }));
    get().fetchEvents();
  },

  setLockedCameraId: (id) => set({ lockedCameraId: id }),

  // Set both time bounds in a SINGLE state update + ONE fetchEvents. Used by the
  // histogram bar-click: calling setFilter('from') then setFilter('to') would
  // fire two reads, and the from-only intermediate request could win the race
  // and show the wrong window. Writing both at once removes that race.
  setTimeWindow: (from, to) => {
    set((state) => ({ filters: { ...state.filters, from, to } }));
    get().fetchEvents();
  },

  // Change the Received sort direction; resets paging + refetches the first page
  // (same reset path as setFilter). The new sort propagates to fetchMore via
  // state, so subsequent pages continue in the chosen order.
  setSort: (value) => {
    set({ sort: value });
    get().fetchEvents();
  },

  // Merge live-streamed events ([6.7]) from the analytics WS into the list:
  // keep only rows matching the active filters, de-dupe by id, order by
  // receivedAt to match the current sort (prepend when desc / append when asc),
  // and bump the current (most-recent) histogram bucket by the number admitted.
  // No-op when nothing matches/survives de-dupe.
  mergeLiveEvents: (incoming) => {
    if (!Array.isArray(incoming) || incoming.length === 0) return;
    const { events, filters, sort, aggregate } = get();

    const seen = new Set(events.map((e) => e.id));
    const fresh = incoming.filter(
      (e) => e && e.id && !seen.has(e.id) && eventMatchesFilters(e, filters)
    );
    if (fresh.length === 0) return;

    const dir = sort === 'asc' ? 1 : -1;
    const ordered = [...fresh].sort(
      (a, b) => dir * (new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime())
    );
    const nextEvents = sort === 'desc' ? [...ordered, ...events] : [...events, ...ordered];

    // Bump the current bucket. aggregate is always ASC by bucketStart (server
    // ORDER BY), so the most-recent bucket is the last element regardless of the
    // list's sort direction. (Bucket rollover self-corrects on the next refetch.)
    let nextAggregate = aggregate;
    if (aggregate.length > 0) {
      nextAggregate = aggregate.slice();
      const last = nextAggregate.length - 1;
      nextAggregate[last] = {
        ...nextAggregate[last],
        count: nextAggregate[last].count + fresh.length,
      };
    }

    set({ events: nextEvents, aggregate: nextAggregate });
  },

  // Event detail drawer selection. No fetch: the detail renders from the row
  // already in `events` (EVENT_SELECT carries every field).
  selectEvent: (id) => set({ selectedEventId: id }),
  clearSelection: () => set({ selectedEventId: null }),

  // Resolve a cameraId to a human label. Components must also subscribe to
  // cameraNames so they re-render once the map loads.
  cameraLabel: (id) => get().cameraNames[id] || id,

  // Load the cameraId -> name map from the discovery devices list (same id
  // space as analytics cameraId). Idempotent: skips once loaded.
  fetchCameraNames: async () => {
    if (get().cameraNamesLoaded) return;
    try {
      const res = await fetchWithTenant('/api/md/devices');
      if (res.ok) {
        const data = await res.json();
        const devices = data.devices || [];
        const map = {};
        for (const d of devices) {
          map[d.id] = d.name || d.hostname || d.ipAddress || d.id;
        }
        set({ cameraNames: map, cameraNamesLoaded: true });
      }
    } catch (err) {
      console.error('[Analytics] Camera names fetch error:', err);
    }
  },

  // Populate the filter dropdowns ([8.3b]). ONE cheap call to /facet-values
  // (enum + VendorTopicMapping vocab) instead of 4 grouped aggregates over the
  // event table -- no AnalyticsEvent scan, so it's flat regardless of row count.
  // cameraId options come from cameraNames (devices), sourced in the panel.
  fetchFacets: async () => {
    try {
      const res = await fetchWithTenant('/api/analytics/facet-values');
      if (!res.ok) return;
      const data = await res.json();
      set({
        facets: {
          categories: Array.isArray(data.eventCategory) ? data.eventCategory : [],
          vendors: Array.isArray(data.vendor) ? data.vendor : [],
          topics: Array.isArray(data.canonicalTopic) ? data.canonicalTopic : [],
        },
      });
    } catch (err) {
      console.error('[Analytics] Facets fetch error:', err);
    }
  },

  // Time-bucketed histogram over the SAME filters as the list (no groupBy, so
  // total counts per bucket). Reuses buildQuery for the filter params; the extra
  // limit param it appends is ignored by the aggregate endpoint. bucket is
  // required by the endpoint (defaults to 'day'). Tenant rides the header.
  fetchAggregate: async (bucket = 'day') => {
    set({ aggLoading: true, aggError: null });
    try {
      // Bucket in the browser's zone so bar labels align with local days/hours.
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const qs = buildQuery(get().filters);
      const res = await fetchWithTenant(
        `/api/analytics/events/aggregate?${qs}&bucket=${bucket}&tz=${encodeURIComponent(tz)}`
      );
      if (res.ok) {
        const data = await res.json();
        set({ aggregate: Array.isArray(data) ? data : [], aggLoading: false });
      } else {
        set({ aggLoading: false, aggError: `HTTP ${res.status}` });
      }
    } catch (err) {
      console.error('[Analytics] Aggregate fetch error:', err);
      set({ aggLoading: false, aggError: err.message });
    }
  },

  // First page: replaces the current event list.
  fetchEvents: async () => {
    set({ loading: true, error: null });
    try {
      const qs = buildQuery(get().filters, { sort: get().sort });
      const res = await fetchWithTenant(`/api/analytics/events?${qs}`);
      if (res.ok) {
        const data = await res.json();
        set({
          events: data.events || [],
          nextCursor: data.nextCursor || null,
          hasMore: !!data.hasMore,
          loading: false,
        });
      } else {
        set({ loading: false, error: `HTTP ${res.status}` });
      }
    } catch (err) {
      console.error('[Analytics] Events fetch error:', err);
      set({ loading: false, error: err.message });
    }
  },

  // Next page via the opaque keyset cursor; appends. No-op without hasMore.
  fetchMore: async () => {
    const { hasMore, nextCursor, loading, filters, events, sort } = get();
    if (!hasMore || !nextCursor || loading) return;
    set({ loading: true, error: null });
    try {
      const qs = buildQuery(filters, { cursor: nextCursor, sort });
      const res = await fetchWithTenant(`/api/analytics/events?${qs}`);
      if (res.ok) {
        const data = await res.json();
        set({
          events: [...events, ...(data.events || [])],
          nextCursor: data.nextCursor || null,
          hasMore: !!data.hasMore,
          loading: false,
        });
      } else {
        set({ loading: false, error: `HTTP ${res.status}` });
      }
    } catch (err) {
      console.error('[Analytics] Events fetchMore error:', err);
      set({ loading: false, error: err.message });
    }
  },

  clear: () =>
    set({
      // [8.3a] reset to the default 30d window (recomputed fresh on each mount,
      // so a long-open tab re-entering the page gets a current window).
      filters: getDefaultFilters(),
      facets: { ...EMPTY_FACETS },
      events: [],
      nextCursor: null,
      hasMore: false,
      loading: false,
      error: null,
      sort: 'desc',
      selectedEventId: null,
      cameraNames: {},
      cameraNamesLoaded: false,
      lockedCameraId: null,
      aggregate: [],
      aggLoading: false,
      aggError: null,
    }),
}));

export default useAnalyticsStore;
